import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Stack, TextField, MenuItem, FormControl, InputLabel, Select, Pagination, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Divider, Card, CardContent
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { fetchCurrencySetting } from '../utils/fetchCurrencySetting';

const CashierSessions = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedSession, setSelectedSession] = useState(null);
  const [currency, setCurrency] = useState('$');

  // Fetch currency setting
  useEffect(() => {
    fetchCurrencySetting().then(curr => {
      // Map currency codes to symbols
      const symbolMap = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹' };
      setCurrency(symbolMap[curr] || curr);
    });
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/cashier-sessions/history', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchSessions();
  }, [token]);

  const filtered = sessions.filter(session => {
    const searchLower = search.toLowerCase();
    const userName = session.user?.username || '';
    const notes = session.notes || '';
    return (
      userName.toLowerCase().includes(searchLower) ||
      notes.toLowerCase().includes(searchLower)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const pagedSessions = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 if search or pageSize changes
  useEffect(() => { setPage(1); }, [search, pageSize]);

  const formatCurrency = (amount) => {
    return amount != null ? `${currency}${amount.toFixed(2)}` : '-';
  };

  const formatDuration = (openTime, closeTime) => {
    if (!closeTime) return 'In Progress';
    const duration = new Date(closeTime) - new Date(openTime);
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Calculate summary statistics
  const totalSessions = sessions.length;
  const openSessions = sessions.filter(s => s.status === 'open').length;
  const closedSessions = sessions.filter(s => s.status === 'closed').length;
  const totalRevenue = sessions.reduce((sum, s) => sum + (s.totalSales || 0), 0);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f0f2f5' }}>
      {/* Left Panel - Sessions List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <ScheduleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              Cashier Sessions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filtered.length} {filtered.length === 1 ? 'session' : 'sessions'} found
            </Typography>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Search Sessions"
              placeholder="Search by cashier or notes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="page-size-label">Show</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Show"
                onChange={e => setPageSize(Number(e.target.value))}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'white' }}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Cashier</TableCell>
                  <TableCell>Opened At</TableCell>
                  <TableCell>Closed At</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Opening</TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Expected</TableCell>
                  <TableCell align="right">Actual</TableCell>
                  <TableCell align="right">Difference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pagedSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        No sessions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedSessions.map((session) => (
                    <TableRow
                      key={session._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedSession(session)}
                    >
                      <TableCell>
                        {session.status === 'open' ? (
                          <Chip 
                            icon={<AccessTimeIcon />} 
                            label="Open" 
                            color="primary" 
                            size="small" 
                          />
                        ) : (
                          <Chip 
                            icon={<CheckCircleIcon />} 
                            label="Closed" 
                            color="success" 
                            size="small" 
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {session.user?.username || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(session.openedAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {session.closedAt ? new Date(session.closedAt).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDuration(session.openedAt, session.closedAt)}</TableCell>
                      <TableCell align="right">{formatCurrency(session.openingCash)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(session.totalSales)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(session.expectedCash)}</TableCell>
                      <TableCell align="right">{formatCurrency(session.actualCash)}</TableCell>
                      <TableCell align="right">
                        {session.cashDifference != null ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: session.cashDifference === 0 
                                ? 'success.main' 
                                : session.cashDifference > 0 
                                ? 'info.main' 
                                : 'error.main',
                              fontWeight: 600
                            }}
                          >
                            {session.cashDifference > 0 ? '+' : ''}{formatCurrency(session.cashDifference)}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        {filtered.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>

      {/* Right Panel - Summary */}
      <Paper 
        elevation={3}
        sx={{ 
          width: { xs: '100%', md: 400 }, 
          display: 'flex', 
          flexDirection: 'column',
          maxHeight: '100%',
          overflow: 'auto'
        }}
      >
        {/* Summary Header */}
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            SUMMARY
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Statistics Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Total Sessions */}
            <Card sx={{ boxShadow: 2, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'primary.lighter',
                    color: 'primary.main'
                  }}>
                    <ScheduleIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Total Sessions
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary.main">
                  {totalSessions}
                </Typography>
              </CardContent>
            </Card>

            {/* Open Sessions */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'info.lighter',
                    color: 'info.main'
                  }}>
                    <AccessTimeIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Open Sessions
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {openSessions}
                </Typography>
              </CardContent>
            </Card>

            {/* Closed Sessions */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'success.lighter',
                    color: 'success.main'
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Closed Sessions
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {closedSessions}
                </Typography>
              </CardContent>
            </Card>

            {/* Total Revenue */}
            <Card sx={{ boxShadow: 1, bgcolor: 'success.lighter' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'success.main',
                    color: 'white'
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Total Revenue
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="success.dark">
                  {formatCurrency(totalRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Info Box */}
          <Card sx={{ bgcolor: 'info.lighter', boxShadow: 0 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="info.dark" fontWeight={500} gutterBottom>
                💡 Quick Tips
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Click on any session to view detailed information
                </Typography>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Monitor cash differences to ensure accuracy
                </Typography>
                <Typography component="li" variant="caption">
                  Keep opening cash consistent for easier tracking
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>


      {/* Session Details Dialog */}
      <Dialog 
        open={!!selectedSession} 
        onClose={() => setSelectedSession(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedSession && (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h6" fontWeight={700}>Session Details</Typography>
                {selectedSession.status === 'open' ? (
                  <Chip icon={<AccessTimeIcon />} label="Open" color="primary" />
                ) : (
                  <Chip icon={<CheckCircleIcon />} label="Closed" color="success" />
                )}
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3}>
                {/* Basic Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Cashier</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedSession.user?.username || '-'}
                  </Typography>
                </Box>

                <Divider />

                {/* Time Info */}
                <Stack direction="row" spacing={4}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Opened At</Typography>
                    <Typography variant="body1">
                      {new Date(selectedSession.openedAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Closed At</Typography>
                    <Typography variant="body1">
                      {selectedSession.closedAt 
                        ? new Date(selectedSession.closedAt).toLocaleString()
                        : 'Still Open'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">
                      {formatDuration(selectedSession.openedAt, selectedSession.closedAt)}
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                {/* Cash Details */}
                <Stack direction="row" spacing={4}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Opening Cash</Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(selectedSession.openingCash)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Expected Cash</Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(selectedSession.expectedCash)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Actual Cash</Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(selectedSession.actualCash)}
                    </Typography>
                  </Box>
                </Stack>

                {/* Cash Difference */}
                {selectedSession.cashDifference != null && (
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 1, 
                      backgroundColor: selectedSession.cashDifference === 0 
                        ? 'success.light' 
                        : selectedSession.cashDifference > 0 
                        ? 'info.light' 
                        : 'error.light'
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">Cash Difference</Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {selectedSession.cashDifference > 0 ? '+' : ''}
                      {formatCurrency(selectedSession.cashDifference)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedSession.cashDifference === 0 
                        ? 'Perfect balance!' 
                        : selectedSession.cashDifference > 0 
                        ? 'Cash over expected' 
                        : 'Cash under expected'}
                    </Typography>
                  </Box>
                )}

                <Divider />

                {/* Sales Summary */}
                <Typography variant="subtitle1" fontWeight={600}>Sales Summary</Typography>
                <Stack direction="row" spacing={4}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Total Transactions</Typography>
                    <Typography variant="h6">{selectedSession.totalTransactions || 0}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Total Sales</Typography>
                    <Typography variant="h6">{formatCurrency(selectedSession.totalSales)}</Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={4}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Cash Sales</Typography>
                    <Typography variant="body1">{formatCurrency(selectedSession.totalCashSales)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Card Sales</Typography>
                    <Typography variant="body1">{formatCurrency(selectedSession.totalCardSales)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Credit Sales</Typography>
                    <Typography variant="body1">{formatCurrency(selectedSession.totalCreditSales)}</Typography>
                  </Box>
                </Stack>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Total Discount</Typography>
                  <Typography variant="body1">{formatCurrency(selectedSession.totalDiscount)}</Typography>
                </Box>

                {/* Notes */}
                {selectedSession.notes && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">{selectedSession.notes}</Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedSession(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CashierSessions;
